import React, { useState, useEffect, useRef, Fragment } from 'react';
import './GoalStatus.css';

const API_BASE = '/api';

function GoalStatus() {
  const [goals, setGoals] = useState([]);
  const [botStatus, setBotStatus] = useState(null);
  const [connected, setConnected] = useState(false);
  const [betResults, setBetResults] = useState({});
  const [acikMarketInfo, setAcikMarketInfo] = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [accountsSummary, setAccountsSummary] = useState([]);

  const [refreshing, setRefreshing] = useState(false);

  const eventSourceRef = useRef(null);

  const refreshBulten = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE}/goals/refresh-bulten`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.error('Refresh error:', err);
    }
    setRefreshing(false);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const fetchInfo = () => {
      fetch(`${API_BASE}/settings/cache`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => {
          if (d.success && d.data.accounts) {
            setAccountsSummary(d.data.accounts);
          }
        })
        .catch(() => {});
    };
    fetchInfo();
    const interval = setInterval(fetchInfo, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const es = new EventSource(`${API_BASE}/goals/stream?token=${token}`);
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        switch (data.type) {
          case 'init':
            setGoals(data.goals || []);
            setBotStatus(data.status || null);
            setConnected(true);
            if (data.betResults) setBetResults(data.betResults);
            if (data.acikMarketInfo) setAcikMarketInfo(data.acikMarketInfo);
            break;
          case 'goal':
            setGoals(prev => {
              const idx = prev.findIndex(g => g.id === data.goal.id);
              if (idx >= 0) return prev.map(g => g.id === data.goal.id ? data.goal : g);
              return [data.goal, ...prev].slice(0, 500);
            });
            break;
          case 'goalCancel':
            setGoals(prev => prev.filter(g => g.id !== data.goal.id));
            break;
          case 'betResult':
            setBetResults(prev => ({ ...prev, [data.goalId]: data.result }));
            break;
          case 'detectionUpdate':
            setGoals(prev => prev.map(g =>
              g.id === data.goalId ? { ...g, detections: data.detections } : g
            ));
            break;
          case 'ambiguousUpdate':
            setGoals(prev => prev.map(g =>
              g.id === data.goalId ? { ...g, ambiguousDetections: data.ambiguousDetections } : g
            ));
            break;
          case 'acikMarketScheduled':
            setAcikMarketInfo(prev => ({ ...prev, [data.goalId]: { scheduled: true, scheduledFor: data.scheduledFor, suresi: data.suresi } }));
            break;
          case 'acikMarketResult':
            setAcikMarketInfo(prev => ({ ...prev, [data.goalId]: { ...prev[data.goalId], scheduled: false, result: data.result, checkedAt: data.checkedAt, suresi: data.suresi || prev[data.goalId]?.suresi, request: data.request, response: data.response } }));
            break;
          default:
            break;
        }
      } catch (err) {
        console.error('SSE parse error:', err);
      }
    };

    es.onerror = () => setConnected(false);
    es.onopen = () => setConnected(true);

    return () => { if (eventSourceRef.current) eventSourceRef.current.close(); };
  }, []);

  /* ── Helpers ── */
  const DAYS_TR = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

  const fmtTime = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    const time = dt.toLocaleTimeString('tr-TR', { timeZone: 'Europe/Istanbul', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const ms = String(dt.getMilliseconds()).padStart(3, '0');
    return `${time}.${ms}`;
  };

  const parseTeam = (full) => {
    const m = full?.match(/^(.+?)\s*\(([^)]+)\)$/);
    return m ? { club: m[1].trim(), player: m[2].trim() } : { club: '', player: full || '?' };
  };

  const getDetectRank = (detections, source) => {
    if (!detections || !detections[source]) return 0;
    const entries = Object.entries(detections).filter(([, v]) => v != null).sort((a, b) => a[1] - b[1]);
    return entries.findIndex(([k]) => k === source) + 1;
  };

  const toggleExpand = (id) => setExpandedRows(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Countdown component for acikMarket
  const AcikMarketCountdown = ({ scheduledFor }) => {
    const [remaining, setRemaining] = useState(0);
    useEffect(() => {
      const update = () => {
        const diff = Math.max(0, Math.ceil((new Date(scheduledFor).getTime() - Date.now()) / 1000));
        setRemaining(diff);
      };
      update();
      const iv = setInterval(update, 1000);
      return () => clearInterval(iv);
    }, [scheduledFor]);
    if (remaining <= 0) return <span className="acik-market-badge acik-market-checking">🔍 Kontrol ediliyor...</span>;
    return <span className="acik-market-badge acik-market-countdown">⏳ {remaining}sn</span>;
  };

  return (
    <div className="goal-status">
      {/* Header */}
      <div className="goal-header">
        <div className="goal-header-left">
          <h2>⚽Otokupon Monitör</h2>
          <span className="goal-count">{goals.length} gol</span>
        </div>
        <div className="goal-header-right">
          <button className="goal-refresh-btn" onClick={refreshBulten} disabled={refreshing}>
            {refreshing ? '⏳' : '🔄'} Yenile
          </button>
          <span className={`connection-badge ${connected ? 'conn-active' : 'conn-inactive'}`}>
            {connected ? '● Canlı' : '○ Bağlantı kesildi'}
          </span>
          {botStatus && <span className="match-count-badge">{botStatus.matchCount} maç izleniyor</span>}
          {accountsSummary.length > 0 && (
            <span className="status-pill" title={accountsSummary.map(a => `${a.displayName || a.username}: ${a.isLoggedIn ? '✅' : '❌'}${a.autoBetEnabled ? ' 🎰' : ''}`).join('\n')}
              style={{ cursor: 'help' }}>
              👥 {accountsSummary.filter(a => a.isLoggedIn).length}/{accountsSummary.length} Giriş
              {accountsSummary.some(a => a.autoBetEnabled) && ` | 🎰 ${accountsSummary.filter(a => a.autoBetEnabled).length}`}
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="goal-table-container">
        <table className="goal-table">
          <thead>
            <tr>
              <th className="th-date">Tarih</th>
              <th className="th-detect">Nesine</th>
              <th className="th-detect">Oley</th>
              <th className="th-detect">Football.com</th>
              <th className="th-home">Ev</th>
              <th className="th-away">Dep</th>
              <th className="th-score">Skor</th>
              <th className="th-total">Toplam Gol</th>
              <th className="th-odds">Sıradaki Gol</th>
              <th className="th-mid">MID</th>
              <th className="th-market">Market</th>
              <th className="th-account">Hesap</th>
              <th className="th-iplay">I.Play</th>
              <th className="th-ival">I.Val</th>
              <th className="th-pplay">P.Play</th>
              <th className="th-pval">P.Val</th>
              <th className="th-barcode">Barkod</th>
            </tr>
          </thead>
          <tbody>
            {goals.length === 0 ? (
              <tr>
                <td colSpan="17" className="no-goals">
                  <div className="no-goals-content">
                    <span className="no-goals-icon">⏳</span>
                    <p>Henüz gol yok. Maçlar devam ederken goller burada görünecek.</p>
                  </div>
                </td>
              </tr>
            ) : goals.map((goal, index) => {
              const betRes = betResults[goal.id];
              const hasBet = !!betRes;
              const am = acikMarketInfo[goal.id];

              // Extract initial group and follower accounts
              const initialAcc = betRes?.accounts?.find(a => a.role === 'initial') || null;
              const coInitialAccs = betRes?.accounts?.filter(a => a.role === 'co-initial') || [];
              const initialGroupAccs = betRes?.accounts?.filter(a => a.role === 'initial' || a.role === 'co-initial') || [];
              const parallelAccs = betRes?.accounts?.filter(a => a.role !== 'initial' && a.role !== 'co-initial') || [];
              // Fallback: initialAccount snapshot from betResult (frozen at goal time)
              const snapshotInitial = betRes?.initialAccount || null;

              const hasBetDetail = hasBet && (betRes.accounts || betRes.betSkipReason || betRes.playRequest || betRes.batches);
              const hasAcikMarketDetail = am && (am.result || am.request || am.response);
              const hasExpandableContent = hasBetDetail || hasAcikMarketDetail;
              const isExpanded = expandedRows.has(goal.id);

              // Step numbering for dropdown
              const hasMarketKontrol = am && (am.result || am.scheduled);

              return (
                <Fragment key={goal.id}>
                  <tr className={`goal-row ${index === 0 ? 'goal-row-new' : ''} ${hasBet ? 'goal-row-has-bet' : ''} ${hasExpandableContent ? 'goal-row-clickable' : ''}`}
                    onClick={() => hasExpandableContent && toggleExpand(goal.id)}>
                    {/* Tarih */}
                    <td className="td-date">
                      {(() => {
                        const dt = new Date(goal.timestamp);
                        const trOpts = { timeZone: 'Europe/Istanbul' };
                        const datePart = dt.toLocaleDateString('tr-TR', { ...trOpts, day: '2-digit', month: '2-digit', year: '2-digit' });
                        const dayName = dt.toLocaleDateString('tr-TR', { ...trOpts, weekday: 'long' });
                        const dayNameShort = DAYS_TR.find(d => dayName.toLowerCase().startsWith(d.toLowerCase())) || dayName;
                        return (
                          <>
                            <span className="date-day">{dayNameShort}</span>
                            <span className="date-dmy">{datePart}</span>
                          </>
                        );
                      })()}
                    </td>
                    {/* Nesine & Oley & Football detection */}
                    {['nesine', 'oley', 'football'].map(source => {
                      const detected = goal.detections?.[source];
                      const nesineTime = goal.detections?.nesine;
                      const rank = getDetectRank(goal.detections, source);
                      let diffLabel = null;
                      if (detected && nesineTime && source !== 'nesine') {
                        const diff = detected - nesineTime;
                        const secs = (diff / 1000).toFixed(1);
                        diffLabel = diff <= 0 ? `${secs}s` : `+${secs}s`;
                      }
                      const playSentAt = source === 'nesine' && betRes?.timing
                        ? (betRes.timing.probeSentAt || betRes.timing.batchSentAt || null)
                        : null;
                      const isAmbiguous = goal.ambiguousDetections?.[source];
                      return (
                        <td key={source} className={`td-detect ${isAmbiguous ? 'detect-ambiguous' : rank ? `detect-rank-${rank}` : ''}`}>
                          {detected ? fmtTime(detected) : isAmbiguous ? '⚠️' : '-'}
                          {diffLabel && (
                            <span className={`detect-diff ${diffLabel.startsWith('-') ? 'diff-fast' : 'diff-slow'}`}>
                              {diffLabel}
                            </span>
                          )}
                          {playSentAt && (
                            <span className="detect-play-sent">
                              🎰 {fmtTime(playSentAt)}
                            </span>
                          )}
                        </td>
                      );
                    })}
                    {/* Ev */}
                    <td className={`td-team ${goal.goalTeam === 'home' ? 'td-scorer-home' : ''}`}>
                      {(() => {
                        const t = parseTeam(goal.homeTeam);
                        return (
                          <div className="team-cell">
                            <span className="team-player">{t.player}</span>
                            {t.club && <span className="team-club">{t.club}</span>}
                          </div>
                        );
                      })()}
                    </td>
                    {/* Dep */}
                    <td className={`td-team ${goal.goalTeam === 'away' ? 'td-scorer-away' : ''}`}>
                      {(() => {
                        const t = parseTeam(goal.awayTeam);
                        return (
                          <div className="team-cell">
                            <span className="team-player">{t.player}</span>
                            {t.club && <span className="team-club">{t.club}</span>}
                          </div>
                        );
                      })()}
                    </td>
                    {/* Skor */}
                    <td className="td-score">
                      <span className="score-display">
                        <span className={goal.goalTeam === 'home' ? 'score-highlight' : ''}>{goal.homeScore}</span>
                        <span className="score-sep">-</span>
                        <span className={goal.goalTeam === 'away' ? 'score-highlight' : ''}>{goal.awayScore}</span>
                      </span>
                    </td>
                    {/* Toplam Gol */}
                    <td className="td-total">
                      <span className="total-badge">{goal.totalGoals}</span>
                    </td>
                    {/* Sıradaki Gol */}
                    <td className="td-odds">
                      {goal.nextGoalMarket ? (
                        <div className="odds-container">
                          <span className="odds-sov">{goal.nextGoalMarket.sov}</span>
                          <div className="odds-row">
                            {goal.nextGoalMarket.outcomes.map((o, i) => {
                              const isMatchGoal = goal.nextGoalMarket.sov === goal.totalGoals;
                              const isScorer = (goal.goalTeam === 'home' && o.label === 'Ev') ||
                                               (goal.goalTeam === 'away' && o.label === 'Dep');
                              return (
                                <span key={i} className={`odds-item ${isMatchGoal && isScorer ? 'odds-match' : ''}`}>
                                  <span className="odds-item-label">{o.label}</span>
                                  <span className="odds-item-value">{o.value?.toFixed(2) || '-'}</span>
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <span className="odds-closed">Kapalı</span>
                      )}
                    </td>
                    {/* MID */}
                    <td className="td-mid">
                      {goal.nextGoalMarket ? (
                        <div className="mid-info">
                          <span className="mid-value">{goal.nextGoalMarket.marketId}</span>
                          <span className="mid-extra">MV:{goal.nextGoalMarket.mv}</span>
                          <span className="mid-extra">EV:{goal.nextGoalMarket.ev}</span>
                        </div>
                      ) : (
                        <span className="odds-closed">-</span>
                      )}
                    </td>
                    {/* Market */}
                    <td className="td-market">
                      {(() => {
                        if (!hasBet) return <span className="market-none">—</span>;
                        if (betRes.betSkipReason === 'noEvent') return <span className="market-badge market-fail" title="Event bulunamadı">❌ Anlık Cache</span>;
                        if (betRes.betSkipReason === 'noMarket') return <span className="market-badge market-fail" title="Market bulunamadı">❌ Anlık Cache</span>;
                        if (betRes.betSkipReason === 'noOutcome') return <span className="market-badge market-fail" title="Outcome bulunamadı">❌ Anlık Cache</span>;
                        if (betRes.betSkipReason === 'oddIs1') return <span className="market-badge market-warn">⚠️ Oran 1.0</span>;
                        if (am?.scheduled) return <AcikMarketCountdown scheduledFor={am.scheduledFor} />;
                        if (am?.result) {
                          if (am.result === 'open') return <span className="market-badge market-open">✅ Açık{am.suresi ? <span className="market-suresi"> ({am.suresi}ms)</span> : null}</span>;
                          if (am.result.startsWith('cache')) return <span className="market-badge market-fail" title={am.result}>❌ Anlık Cache</span>;
                          if (am.result.startsWith('delayCache')) return <span className="market-badge market-fail" title={am.result}>❌ Gecikmeli</span>;
                          if (am.result.startsWith('http')) return <span className="market-badge market-fail" title={am.result}>❌ HTTP</span>;
                          return <span className="market-badge market-fail">❌ {am.result}</span>;
                        }
                        if (betRes.betSkipReason === 'marketCheckClosed') return <span className="market-badge market-fail">❌ Kapalı</span>;
                        if (betRes.betSkipReason === 'autoBetDisabled' || betRes.betSkipReason === 'notLoggedIn') return <span className="market-badge market-ok">✅ Cache</span>;
                        return <span className="market-badge market-ok">✅ Cache</span>;
                      })()}
                    </td>
                    {/* Hesap */}
                    <td className="td-account">
                      {initialGroupAccs.length > 0 ? (
                        <>
                          <span className="gs-acc-tag">{initialAcc?.displayName || initialAcc?.username || '?'}</span>
                          {coInitialAccs.length > 0 && <span className="gs-acc-co-count">+{coInitialAccs.length}</span>}
                        </>
                      ) : snapshotInitial ? <span className="gs-acc-tag">{snapshotInitial.displayName || snapshotInitial.username}</span> : '—'}
                    </td>
                    {/* I.Play */}
                    <td className="td-iplay">
                      {(() => {
                        if (!hasBet || initialGroupAccs.length === 0) return '—';
                        return initialGroupAccs.map((acc, i) => {
                          if (acc.betSkipReason) return <div key={i} className="pv-line"><span className="pv-badge pv-skip">⏭️</span></div>;
                          if (acc.playErrorCode) return <div key={i} className="pv-line"><span className={`pv-errcode ${acc.success ? 'pv-errcode-resolved' : 'pv-errcode-fail'}`}>{acc.playErrorCode}</span></div>;
                          if (acc.playResponse || acc.batches?.length) return <div key={i} className="pv-line"><span className="pv-badge pv-ok">✅</span></div>;
                          if (acc.error) return <div key={i} className="pv-line"><span className="pv-badge pv-fail">❌</span></div>;
                          return <div key={i} className="pv-line">—</div>;
                        });
                      })()}
                    </td>
                    {/* I.Val */}
                    <td className="td-ival">
                      {(() => {
                        if (!hasBet || initialGroupAccs.length === 0) return '—';
                        return initialGroupAccs.map((acc, i) => {
                          if (acc.betSkipReason) return <div key={i} className="pv-line">—</div>;
                          if (acc.validateSuccess === true) return <div key={i} className="pv-line"><span className="pv-badge pv-ok">✅</span></div>;
                          if (acc.validateSuccess === false) return <div key={i} className="pv-line"><span className="pv-errcode pv-errcode-fail">{acc.validateErrorCode || '❌'}</span></div>;
                          return <div key={i} className="pv-line">—</div>;
                        });
                      })()}
                    </td>
                    {/* P.Play */}
                    <td className="td-pplay">
                      {(() => {
                        if (!hasBet || parallelAccs.length === 0) return '—';
                        return parallelAccs.map((acc, i) => {
                          if (acc.betSkipReason) return <div key={i} className="pv-line"><span className="pv-badge pv-skip">⏭️</span></div>;
                          if (acc.playErrorCode) return <div key={i} className="pv-line"><span className={`pv-errcode ${acc.success ? 'pv-errcode-resolved' : 'pv-errcode-fail'}`}>{acc.playErrorCode}</span></div>;
                          if (acc.playResponse || acc.batches?.length) return <div key={i} className="pv-line"><span className="pv-badge pv-ok">✅</span></div>;
                          if (acc.error) return <div key={i} className="pv-line"><span className="pv-badge pv-fail">❌</span></div>;
                          return <div key={i} className="pv-line">—</div>;
                        });
                      })()}
                    </td>
                    {/* P.Val */}
                    <td className="td-pval">
                      {(() => {
                        if (!hasBet || parallelAccs.length === 0) return '—';
                        return parallelAccs.map((acc, i) => {
                          if (acc.betSkipReason) return <div key={i} className="pv-line">—</div>;
                          if (acc.validateSuccess === true) return <div key={i} className="pv-line"><span className="pv-badge pv-ok">✅</span></div>;
                          if (acc.validateSuccess === false) return <div key={i} className="pv-line"><span className="pv-errcode pv-errcode-fail">{acc.validateErrorCode || '❌'}</span></div>;
                          return <div key={i} className="pv-line">—</div>;
                        });
                      })()}
                    </td>
                    {/* Barkod */}
                    <td className="td-barcode">
                      {(() => {
                        if (!hasBet) return '—';
                        const allAccounts = betRes.accounts || [];
                        const withBarcode = [...allAccounts].sort((a, b) => {
                          const roleOrder = { initial: 0, 'co-initial': 1, follower: 2, parallel: 3 };
                          return (roleOrder[a.role] ?? 9) - (roleOrder[b.role] ?? 9);
                        }).filter(a => a.barcode);
                        if (withBarcode.length > 0) {
                          return withBarcode.map((a, i) => <span key={i} className="bet-barcode-cell" title={a.displayName || a.username}>{a.barcode}</span>);
                        }
                        if (betRes.barcode) return <span className="bet-barcode-cell">{betRes.barcode}</span>;
                        return '—';
                      })()}
                    </td>
                  </tr>

                  {/* Flow Detail Expand */}
                  {hasExpandableContent && isExpanded && (
                    <tr className="bet-detail-row">
                      <td colSpan="17">
                        <div className={`bet-detail-panel ${betRes?.betSkipReason === 'marketCheckClosed' ? 'bet-panel-market-kontrol' : betRes?.success ? 'bet-panel-success' : hasBet ? 'bet-panel-fail' : 'bet-panel-info'}`}>

                          {/* Adım 1: Anlık Cache */}
                          {hasBet && (
                            <div className="flow-step">
                              <div className="flow-step-header">
                                <span className="flow-step-num">1</span>
                                <span className="flow-step-title">Anlık Cache</span>
                                {betRes.betSkipReason === 'noEvent' ? <span className="flow-badge flow-badge-fail">❌ Event bulunamadı</span>
                                : betRes.betSkipReason === 'noMarket' ? <span className="flow-badge flow-badge-fail">❌ Market bulunamadı</span>
                                : betRes.betSkipReason === 'noOutcome' ? <span className="flow-badge flow-badge-fail">❌ Outcome bulunamadı</span>
                                : betRes.betSkipReason === 'oddIs1' ? <span className="flow-badge flow-badge-warn">⚠️ Oran 1.0</span>
                                : <span className="flow-badge flow-badge-ok">✅ Event + Market + Outcome bulundu</span>}
                                <span className="mk-params">EV:{goal.matchCode} MTID:634 SOV:{goal.totalGoals} N:{goal.goalTeam === 'home' ? 1 : 3}{goal.nextGoalMarket ? ` MV:${goal.nextGoalMarket.mv} MID:${goal.nextGoalMarket.marketId}` : ''}</span>
                              </div>
                            </div>
                          )}

                          {/* Adım 2: Market Kontrol */}
                          {hasMarketKontrol && (
                            <div className="flow-step">
                              <div className="flow-step-header">
                                <span className="flow-step-num">2</span>
                                <span className="flow-step-title">Market Kontrol{am.suresi ? ` (${am.suresi}ms)` : ''}</span>
                                {am.scheduled ? <span className="flow-badge flow-badge-pending">⏳ Bekleniyor</span>
                                : am.result === 'open' ? <span className="flow-badge flow-badge-ok">✅ Açık</span>
                                : am.result?.startsWith('cache') ? <span className="flow-badge flow-badge-fail">❌ Anlık Cache: {am.result.replace('cache', '')}</span>
                                : am.result?.startsWith('delayCache') ? <span className="flow-badge flow-badge-fail">❌ Gecikmeli Cache: {am.result.replace('delayCache', '')}</span>
                                : am.result?.startsWith('http') ? <span className="flow-badge flow-badge-fail">❌ HTTP: {am.result.replace('http', '')}</span>
                                : <span className="flow-badge flow-badge-fail">❌ {am.result}</span>}
                                <span className="mk-params">EV:{goal.matchCode} MTID:634 SOV:{goal.totalGoals} N:{goal.goalTeam === 'home' ? 1 : 3} MV:{goal.nextGoalMarket?.mv || '?'} MID:{goal.nextGoalMarket?.marketId || '?'}</span>
                              </div>
                              {am.request && (
                                <div className="flow-step-body">
                                  <div className="bet-section"><h4>Request</h4><pre>{JSON.stringify(am.request, null, 2)}</pre></div>
                                  <div className="bet-section"><h4>Response</h4><pre>{JSON.stringify(am.response, null, 2)}</pre></div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Durum mesajı (hesap yoksa, cache + market kontrol geçtiyse) */}
                          {hasBet && !betRes.accounts && !['noEvent','noMarket','noOutcome','oddIs1','marketCheckClosed'].includes(betRes.betSkipReason) && (
                            <div className="flow-step">
                              <div className="flow-step-header">
                                <span className="flow-step-num">{hasMarketKontrol ? '3' : '2'}</span>
                                <span className="flow-step-title">Hesap Durumu</span>
                                {betRes.betSkipReason === 'notLoggedIn' ? <span className="flow-badge flow-badge-warn">🔒 Giriş Yapılmamış</span>
                                : betRes.betSkipReason === 'autoBetDisabled' ? <span className="flow-badge flow-badge-warn">⏸️ Oto Kupon Kapalı</span>
                                : betRes.betSkipReason === 'sessionExpired_401' ? <span className="flow-badge flow-badge-warn">🔒 Oturum Doldu (401)</span>
                                : betRes.success ? <span className="flow-badge flow-badge-ok">✅ Başarılı{betRes.barcode ? ` — ${betRes.barcode}` : ''}</span>
                                : <span className="flow-badge flow-badge-fail">❌ Başarısız{betRes.error ? ` — ${betRes.error}` : ''}</span>}
                              </div>
                              {(betRes.playRequest || betRes.batches?.[0]?.playRequest) && (
                                <div className="flow-step-body">
                                  <div className="bet-section"><h4>Play Request</h4><pre>{JSON.stringify(betRes.batches?.[0]?.playRequest || betRes.playRequest, null, 2)}</pre></div>
                                  <div className="bet-section"><h4>Play Response</h4><pre>{JSON.stringify(betRes.batches?.[0]?.playResponse || betRes.playResponse, null, 2)}</pre></div>
                                  {(betRes.validateRequest || betRes.batches?.[0]?.validateRequest) && (
                                    <>
                                      <div className="bet-section"><h4>Validate Request</h4><pre>{JSON.stringify(betRes.batches?.[0]?.validateRequest || betRes.validateRequest, null, 2)}</pre></div>
                                      <div className="bet-section"><h4>Validate Response</h4><pre>{JSON.stringify(betRes.batches?.[0]?.validateResponse || betRes.validateResponse, null, 2)}</pre></div>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Initial Grup (initial + co-initials) */}
                          {initialGroupAccs.length > 0 && (
                            <div className="flow-step">
                              <div className="flow-step-header">
                                <span className="flow-step-num">{hasMarketKontrol ? '3' : '2'}</span>
                                <span className="flow-step-title">🎯 Initial Grup ({initialGroupAccs.length})</span>
                                {(() => {
                                  const played = initialGroupAccs.filter(a => !a.betSkipReason);
                                  const ok = played.filter(a => a.success).length;
                                  if (played.length === 0) return <span className="flow-badge flow-badge-skip">⏭️ Tümü atlandı</span>;
                                  return ok > 0
                                    ? <span className="flow-badge flow-badge-ok">✅ {ok}/{played.length}</span>
                                    : <span className="flow-badge flow-badge-fail">❌ 0/{played.length}</span>;
                                })()}
                              </div>
                              {initialGroupAccs.map((acc, ai) => (
                                <div key={ai} className="flow-sub-account">
                                  <div className="flow-sub-header">
                                    <strong>{acc.role === 'initial' ? '🎯 ' : '🔗 '}{acc.displayName || acc.username}</strong>
                                    <span className="flow-role-tag">{acc.role === 'initial' ? 'initial' : 'co-initial'}</span>
                                    {acc.betSkipReason ? <span className="flow-badge flow-badge-skip">⏭️ {acc.betSkipReason}</span>
                                    : acc.success ? <span className="flow-badge flow-badge-ok">✅{acc.barcode ? ` Barkod: ${acc.barcode}` : ''}</span>
                                    : <span className="flow-badge flow-badge-fail">❌{acc.error ? ` ${acc.error}` : ''}{acc.playErrorCode ? ` (${acc.playErrorCode})` : ''}</span>}
                                    {acc.odd && <span className="flow-acc-meta">Oran: {acc.odd} | {acc.amount}₺{acc.potentialWin ? ` | Kazanç: ${acc.potentialWin}₺` : ''}</span>}
                                  </div>
                                  {!acc.betSkipReason && (
                                    <div className="flow-step-body">
                                      {(acc.playRequest || acc.batches?.[0]?.playRequest) && (
                                        <>
                                          <div className="bet-section"><h4>Play Request</h4><pre>{JSON.stringify(acc.batches?.[0]?.playRequest || acc.playRequest, null, 2)}</pre></div>
                                          <div className="bet-section"><h4>Play Response</h4><pre>{JSON.stringify(acc.batches?.[0]?.playResponse || acc.playResponse, null, 2)}</pre></div>
                                        </>
                                      )}
                                      {(acc.validateRequest || acc.batches?.[0]?.validateRequest) && (
                                        <>
                                          <div className="bet-section"><h4>Validate Request</h4><pre>{JSON.stringify(acc.batches?.[0]?.validateRequest || acc.validateRequest, null, 2)}</pre></div>
                                          <div className="bet-section"><h4>Validate Response</h4><pre>{JSON.stringify(acc.batches?.[0]?.validateResponse || acc.validateResponse, null, 2)}</pre></div>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Paralel Hesaplar (Follower) */}
                          {parallelAccs.length > 0 && parallelAccs.some(a => !a.betSkipReason || a.playRequest || a.batches?.length) && (
                            <div className="flow-step">
                              <div className="flow-step-header">
                                <span className="flow-step-num">{hasMarketKontrol ? (initialGroupAccs.length > 0 ? '4' : '3') : (initialGroupAccs.length > 0 ? '3' : '2')}</span>
                                <span className="flow-step-title">👥 Paralel Hesaplar ({parallelAccs.length})</span>
                                {(() => {
                                  const played = parallelAccs.filter(a => !a.betSkipReason);
                                  const ok = played.filter(a => a.success).length;
                                  if (played.length === 0) return <span className="flow-badge flow-badge-skip">⏭️ Tümü atlandı</span>;
                                  return ok > 0
                                    ? <span className="flow-badge flow-badge-ok">✅ {ok}/{played.length}</span>
                                    : <span className="flow-badge flow-badge-fail">❌ 0/{played.length}</span>;
                                })()}
                              </div>
                              {parallelAccs.map((acc, ai) => (
                                <div key={ai} className="flow-sub-account">
                                  <div className="flow-sub-header">
                                    <strong>{acc.displayName || acc.username}</strong>
                                    {acc.betSkipReason ? <span className="flow-badge flow-badge-skip">⏭️ {acc.betSkipReason}</span>
                                    : acc.success ? <span className="flow-badge flow-badge-ok">✅{acc.barcode ? ` Barkod: ${acc.barcode}` : ''}</span>
                                    : <span className="flow-badge flow-badge-fail">❌{acc.error ? ` ${acc.error}` : ''}{acc.playErrorCode ? ` (${acc.playErrorCode})` : ''}</span>}
                                    {acc.odd && <span className="flow-acc-meta">Oran: {acc.odd} | {acc.amount}₺</span>}
                                  </div>
                                  {!acc.betSkipReason && (
                                    <div className="flow-step-body">
                                      {(acc.playRequest || acc.batches?.[0]?.playRequest) && (
                                        <>
                                          <div className="bet-section"><h4>Play Request</h4><pre>{JSON.stringify(acc.batches?.[0]?.playRequest || acc.playRequest, null, 2)}</pre></div>
                                          <div className="bet-section"><h4>Play Response</h4><pre>{JSON.stringify(acc.batches?.[0]?.playResponse || acc.playResponse, null, 2)}</pre></div>
                                        </>
                                      )}
                                      {(acc.validateRequest || acc.batches?.[0]?.validateRequest) && (
                                        <>
                                          <div className="bet-section"><h4>Validate Request</h4><pre>{JSON.stringify(acc.batches?.[0]?.validateRequest || acc.validateRequest, null, 2)}</pre></div>
                                          <div className="bet-section"><h4>Validate Response</h4><pre>{JSON.stringify(acc.batches?.[0]?.validateResponse || acc.validateResponse, null, 2)}</pre></div>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default GoalStatus;
